/* jshint esversion: 6 */

/**
 * RichTextEditorDirective class provides a directive for managing rich text editors.
 * It integrates various services and configurations to handle the rich text editing functionality.
 */

import * as CKEDITOR from 'ckeditor4';

import RichTextEditorService from '../../../services/core/RichTextEditorService';

class RichTextEditorDirective {
    /**
     * Constructor for RichTextEditorDirective.
     * @param {Object} $rootScope - AngularJS root scope.
     * @param {Object} $injector - AngularJS injector service.
     * @param {jQuery} $ - jQuery instance.
     * @param {Object} $window - AngularJS window service.
     * @param {Object} $bridges - Bridge services.
     * @param {Object} $translate - Translation service.
     * @param {Object} $config - Configuration service.
     * @param {Object} BridgeRuntimeService - Bridge runtime service.
     * @param {Object} InactivityTimeoutService - Inactivity timeout service.
     * @param {Object} IAAPreferencesService - IAA preferences service.
     * @param {Object} TaskCountdownService - Task countdown service.
     * @param {Object} $timeout - AngularJS timeout service.
     * @param {Object} dialogs - Dialog service.
     */
    constructor($rootScope, $injector, $, $window, $bridges, $translate, $config, BridgeRuntimeService, InactivityTimeoutService, IAAPreferencesService, TaskCountdownService, $timeout, dialogs) {
        this.$rootScope = $rootScope;
        this.$injector = $injector;
        this.$ = $;
        this.$window = $window;
        this.$bridges = $bridges;
        this.$translate = $translate;
        this.$config = $config;
        this.BridgeRuntimeService = BridgeRuntimeService;
        this.InactivityTimeoutService = InactivityTimeoutService;
        this.IAAPreferencesService = IAAPreferencesService;
        this.TaskCountdownService = TaskCountdownService;
        this.$timeout = $timeout;
        this.dialogs = dialogs;
    }

    /**
     * Link function to initialize the directive.
     * @param {Object} scope - AngularJS scope.
     * @param {jQuery} element - jQuery element.
     * @param {Object} attrs - Attributes.
     * @param {Object} controller - Controller.
     */
    link(scope, element, attrs, controller) {
        const TAG = "[richTextEditorDirective]";
        let showLoadingErrorTime = 5000;
        let showLoadingErrorTimeout;

        scope.ckconfig = null;
        scope.loaded = false;
        scope.showLoadingError = false;
        const $logger = this.$bridges.logger();
        scope._logIdentifier = 'richTextEditorDirective';

        const TaskInfoService = this.$injector.get('TaskInfoService');
        const taskInfo = TaskInfoService.getTaskInfo();
        const taskContent = this.TaskCountdownService.getInstance();

        let uuid;
        const assessmentLanguage = taskInfo.getLanguageCode();
        const imageFromCalc = angular.isDefined(attrs.imageFromCalc);
        const placeholder = attrs.placeholder;
        const objType = attrs.objtype;
        const hidebar = typeof attrs.hideBar !== 'undefined' && attrs.hideBar ? true : false;
        const wordcount = typeof attrs.wordcount !== 'undefined' && ((attrs.wordcount === true) || (attrs.wordcount === "true")) ? true : false;
        const selectableStyles = typeof attrs.selectableStyles !== 'undefined' && attrs.selectableStyles ? true : false;
        const spellcheck = typeof attrs.spellcheck !== 'undefined' && ((attrs.spellcheck === true) || (attrs.spellcheck === "true")) ? true : false;
        const introductionPage = typeof attrs.introductionPage !== 'undefined' ? true : false;
        const tooltip = angular.isDefined(attrs.tooltip);
        const source = angular.isDefined(attrs.source);
        const width = attrs.width;
        let config = {};

        attrs.$set('id', attrs.id);
        attrs.$set('placeholder', placeholder);

        if (attrs.id) {
            uuid = attrs.id.match('^cke-') ? attrs.id.substring(4) : attrs.id;
        }

        if (!controller) {
            $logger.error('No Model was found when trying to create a RichTextEditor instance');
            return;
        }

        const originalHeight = angular.isNumber(scope.height) ? parseInt(scope.height) : false;

        let editor;
        const RTEService = this.$injector.instantiate(RichTextEditorService);

        const _initToolBarsAndPlugins = () => {
            if (isCMS && !this.$config.isPreviewMode()) {
                const _ibToolBar = [
                    ['Bold', 'Italic', '-', 'Undo', 'Redo'],
                    ['Underline', 'Subscript', 'Superscript'],
                    ['SpecialChar', 'SpecialCharSetting', 'WirisEditor'],
                ];
                RTEService.addToolbar('SpecialCharSetting');
                RTEService.addPlugin('specialcharsetting');
            }

            if (angular.isDefined(scope.contentObject) &&
                scope.contentObject.hasOwnProperty('getPreferences') &&
                scope.contentObject.getPreferences().hasOwnProperty('specialChars')
            ) {
                let specialChars = scope.contentObject.getPreferences().specialChars;
                if (!specialChars) {
                    specialChars = ['&#x3b1;', '&#x3b2;', '&#x3b3;', '&#x3bb;', '&#x3bc;', '&#x3c1;', '&#x3a9;', '&#x3c0;', '&#x2211;', '&#x2206;', '&#x3b8;'];
                    scope.contentObject.getPreferences().specialChars = specialChars;
                }
                RTEService.set('specialChars', specialChars);
            }

            if (wordcount) {
                RTEService.enableWordCount();
            }
            if (tooltip) {
                RTEService.enableToolTip();
            }

            RTEService.enableIntroductionPage(introductionPage);
            if (introductionPage) {
                // Do nothing
            } else {
                RTEService.addToolbar('Styles');
            }

            if (imageFromCalc) {
                RTEService.enableImageFromCalc(true);
                RTEService.set(
                    'imageFromCalcLabel',
                    this.$translate.instant('CONTENTOBJECT.RESPONSETEXTBOX.IMAGE-FROM-CALC')
                );
            }

            if (angular.isDefined(scope.formbtns) && (scope.formbtns == '1' || scope.formbtns === true) && objType === RTEService.OBJECT_TYPE.MCQ) {
                RTEService.addToolbar([ 'Checkbox', 'Radio', 'Select', 'TextField' ]);
                RTEService.addPlugin('forms');
            }

            RTEService.removePlugin('elementspath');
            RTEService.removePlugin('image');

            if (objType === RTEService.OBJECT_TYPE.RESPONSE) {
                RTEService.removeToolbar('Source');
            }

            if (isCMS) {
                if (source) {
                    RTEService.addToolbar('Source');
                }
            }
        };

        const _initLocalListeners = () => {
            attrs.$observe('placeholder', (newVal) => {
                if (typeof editor !== 'undefined') {
                    const root = (editor.editable ? editor.editable() : (editor.mode == 'wysiwyg' ? editor.document && editor.document.getBody() : editor.textarea));
                    if (angular.isDefined(root)) {
                        root.setHtml(newVal);
                    }
                }
            });

            scope.$on('$destroy', () => {
                if (typeof editor !== 'undefined' && editor) {
                    try {
                        console.log("Destroying CKE", editor.name);
                        editor.destroy();
                        editor = undefined;
                    } catch (e) {
                        // Ignore error
                    }
                }
            });
        };

        const _initStyles = () => {
            RTEService.addStyleSet({
                name: this.$translate.instant('CONTENTOBJECT.RESPONSETEXTBOX.HIGHLIGHT'),
                element: 'span',
                attributes: {'class': 'highlighted'}
            });

            if (selectableStyles) {
                RTEService.addStyleSet({
                    name: this.$translate.instant('CONTENTOBJECT.RESPONSETEXTBOX.QUESTION-HIGHLIGHT'),
                    element: 'span',
                    attributes: {'class': 'question-highlight'}
                });
            }
        };

        const _startDirective = (_config) => {
            if (scope.inlineEditor) {
                RTEService.set('inlineEditor', true);
                attrs.$set('contenteditable', true);
            }

            if (scope.height) {
                RTEService.set('originalHeight', originalHeight);
                RTEService.set('height', originalHeight);
                RTEService.set('autoGrow_minHeight', originalHeight);
            }

            _initLocalListeners();
            _initStyles();
            _initToolBarsAndPlugins();
            RTEService.initLanguage(isCMS, this.$config.isPreviewMode(), this.$rootScope.isJournal);

            if (spellcheck) {
                RTEService.enableSpellcheck(attrs.id, true);
            }

            return RTEService.getEditor(element[0], RTEService.getConfig());
        };

        const startWatchingHeight = (editor) => {
            if (!originalHeight) {
                return;
            }
            scope.$watch('height', function () {
                const h = parseInt(scope.height);
                if (h === originalHeight) {
                    return;
                }
                editor.config.height = h;
                editor.config.autoGrow_minHeight = h;
                editor.resize("100%", h, true);
            });
        };

        const setColorTheme = (e) => {
            if (this.$rootScope.currentColorThemeObj && this.$rootScope.currentColorThemeObj.class && this.$rootScope.currentColorThemeObj.index !== "00") {
                this.$timeout(() => {
                    const editorBody = e.editor.document.getBody();
                    editorBody.setAttribute('class', editorBody.getAttribute('class') + ' ' + this.$rootScope.currentColorThemeObj.class);
                });
            }
        };

        const setEditorListener = (editor) => {
            editor.on('instanceReady', (e) => {
                scope.loaded = true;
                clearTimeout(showLoadingErrorTimeout);
                e.editor.document.getBody().setAttribute('uuid', scope.contentObject.getUUID());

                if (!isCMS && taskContent.getMode() === 'readingTime') {
                    e.editor.setReadOnly(true);
                }

                let specialcharsetting;
                e.editor.editable().editor.toolbar.find((toolbar) => {
                    toolbar.items.find((item) => {
                        if (item.name === 'specialcharsetting') {
                            specialcharsetting = item;
                            return true;
                        }
                    });
                });

                if (specialcharsetting) {
                    specialcharsetting.setState(2);
                    e.editor.getCommand('specialcharsetting').enable();
                }

                if (isCMS) {
                    e.editor.on('changeSpecialChars', () => {
                        scope.contentObject.getPreferences().specialChars = e.editor.config.specialChars;
                        scope.contentObject.flagAsDirty();
                    });
                }

                if (spellcheck && e.editor.hasOwnProperty('changePreferences') && angular.isFunction(e.editor.changePreferences)) {
                    e.editor.changePreferences(scope.$parent.$parent.preferencesValues);
                }

                e.editor.on('key', () => {
                    globalScroll();
                });

                let lastGlobalScrollTS = new Date().getTime();
                const globalScroll = () => {
                    if (!e.editor.focusManager.hasFocus) return false;

                    // Debounce on every keystroke before doing any work. Keep this
                    // ahead of the selection/range lookup so the hot path (a held-key
                    // burst) stays cheap.
                    const globalScrollDeltaMS = new Date().getTime() - lastGlobalScrollTS;
                    if (globalScrollDeltaMS < 500) {
                        return false;
                    }

                    // Focus and selection are independent in CKEditor: the editor can
                    // have focus but no established cursor (e.g., right after a
                    // programmatic focus, or on the first keystroke after a state
                    // transition like reading→writing time). Without these guards,
                    // every keystroke in that window throws
                    //   "Cannot read properties of undefined (reading 'startContainer')"
                    // (Sentry IB-ASSESSMENT-FRONTEND-MD/JJ).
                    const selection = e.editor.getSelection();
                    if (!selection) return false;
                    const ranges = selection.getRanges();
                    if (!ranges || !ranges.length) return false;
                    const startContainer = ranges[0] && ranges[0].startContainer;
                    if (!startContainer || !startContainer.$) return false;

                    let minViewH = $('body').scrollTop() ? $('body').scrollTop() : $('html').scrollTop();
                    minViewH += 50;
                    let maxViewH = minViewH + $(window).height();
                    maxViewH -= 100;

                    let editorBodyScrollTop = $(e.editor.document.$).find('body').scrollTop() ? $(e.editor.document.$).find('body').scrollTop() : $(e.editor.document.$).find('html').scrollTop();
                    let topBarHeight = $("#" + e.editor.name).parent().find('.cke_top').height();
                    let editorOffsetTop = $("#" + e.editor.name).parent().offset().top;

                    const $cursorEl = $(startContainer.$);
                    const $cursorParent = $cursorEl.parent();
                    const parentOffsetTop = $cursorParent.offset().top;
                    let cursorPos = parentOffsetTop < 2 ? $cursorEl.offset().top : parentOffsetTop;
                    cursorPos = editorOffsetTop + cursorPos - editorBodyScrollTop + topBarHeight;
                    let cursorElementHeight = parentOffsetTop < 2 ? $cursorEl.height() : $cursorParent.height();
                    cursorPos += cursorElementHeight;

                    if (cursorPos < minViewH || cursorPos > maxViewH) {
                        if (minViewH - cursorPos > 0) {
                            $('body, html').scrollTop(minViewH - (minViewH - cursorPos) - 50);
                        } else {
                            $('body, html').scrollTop(minViewH + (cursorPos - maxViewH));
                        }
                    }

                    lastGlobalScrollTS = new Date().getTime();
                };

                let onChangeTimeoutID = -1;
                e.editor.on('change', (ev) => {
                    const onCkeditorChange = () => {
                        const value = ev.editor.getData();

                        if (isCMS) {
                            controller.$setViewValue(value);
                        }

                        if (scope.change) {
                            scope.change({ e: { value: value, ckeditor: ev.editor, originalEvent: ev } });
                        }

                        if (!value && ev.editor.plugins.wordcount) {
                            ev.editor.plugins.wordcount.wordCount = 0;
                            $('#cke_wordcount_' + ev.editor.name).html("&nbsp;");
                        }
                        setColorTheme(e);
                        this.InactivityTimeoutService.resetIdle('onCkeditorChange');
                    };

                    if (isCMS) {
                        scope.$apply(() => {
                            onCkeditorChange();
                        });
                        return;
                    }

                    if (onChangeTimeoutID > -1) {
                        this.$window.clearTimeout(onChangeTimeoutID);
                    }

                    onChangeTimeoutID = this.$window.setTimeout(() => { scope.$apply(onCkeditorChange); }, 1000, true);
                });

                if (!isCMS || this.$config.isPreviewMode()) {
                    setColorTheme(e);
                }

                e.editor.removeMenuItem('image');

                const setLastKeyboardLanguage = () => {
                    this.$bridges.runtime().setLastKeyboardLanguage();
                };
                e.editor.on('focus', setLastKeyboardLanguage);

                startWatchingHeight(e.editor);

                const addDyslexicFontClass = () => {
                    $('iframe').contents().find('.cke_editable').addClass('cke_dyslexia');
                };
                if (this.IAAPreferencesService.getPreference('dyslexicFont')) {
                    addDyslexicFontClass();
                    this.$timeout(addDyslexicFontClass, 150);
                }

                editor.on('doubleclick', (event) => {
                    if (introductionPage) {
                        if (event.data.element.$.textContent !== 'undefined') {
                            const matches = event.data.element.$.textContent.match(/\{\{([^\{\}])+\}\}/);
                            if (matches && matches[0] === matches.input) {
                                event.stop();
                            }
                        }
                    }

                    if (event.data.element.hasClass('image-from-calculator')) {
                        event.stop();
                    }
                }, null, null, 1);

                editor.on('paste', (event) => {
                    const pasteData = event.data.dataValue;
                    if (pasteData.match(/(<|&lt;)math.*MathML.*(>|&gt;).*(<|&lt;)\/math(>|&gt;)/)) {
                        event.stop();
                    }
                });

                editor.on('pasteState', () => {
                    if (!isCMS) {
                        return;
                    }
                    return scope.$apply(() => {
                        return controller.$setViewValue(editor.getData());
                    });
                });
            });
        };

        attrs.$observe('placeholder', (newVal) => {
            if (!editor) return;
            const root = (editor.editable ? editor.editable() : (editor.mode === 'wysiwyg' ? editor.document && editor.document.getBody() : editor.textarea));
            if (angular.isDefined(root)) {
                root.setHtml(newVal);
            }
        });

        scope.$on('$destroy', () => {
            if (typeof editor !== 'undefined' && editor) {
                try {
                    console.log("Destroying CKE", editor.name);
                    editor.destroy();
                    editor = undefined;
                } catch (e) {
                    // Ignore error
                }
            }
        });

        const langISO = this.$rootScope.isJournal ? this.$translate.preferredLanguage() : taskInfo.getISOInputLanguageCode();
        editor = _startDirective(RTEService.init(objType, hidebar, langISO.substr(0, 2)));

        setEditorListener(editor);

        showLoadingErrorTimeout = setTimeout(() => {
            scope.showLoadingError = true;
        }, showLoadingErrorTime);

        const resetIllegalUndo = () => {
            let illegalSnapShotFound = false;
            for (let snapIdx in editor.undoManager.snapshots) {
                const snap = editor.undoManager.snapshots[snapIdx];
                if (typeof (snap.contents) === 'undefined') {
                    continue;
                }
                if (snap.contents.contains('<textarea richtexteditor="true"')) {
                    illegalSnapShotFound = true;
                }
            }

            if (illegalSnapShotFound) {
                editor.undoManager.currentImage = null;
                editor.undoManager.snapshots = [];
                editor.undoManager.hasUndo = false;
                editor.undoManager.onChange();
            }
        };

        controller.$render = () => {
            editor.setData(element.val());
            editor.on("saveSnapshot", () => {
                resetIllegalUndo();
            });
        };
    }
}

export default RichTextEditorDirective;